/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// collection page anchor
class AutoScrollToAnchor extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    window.addEventListener("DOMContentLoaded", () => {
      this.scrollToAnchorInUrl();
    });
  }

  scrollToAnchorInUrl() {
    const hash = window.location.hash;
    if (hash) {
      const targetId = hash.split("_")[2];
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "auto",
          block: "center",
          inline: "nearest",
        });
        targetElement.classList.add("anchor-highlighted");
      }
    }
  }
}
customElements.define("auto-scroll-to-anchor", AutoScrollToAnchor);

// js/custom-element/behavior/track-button.js
class JumpButton extends HTMLButtonElement {
  connectedCallback() {
    this.addEventListener("click", () => {
      if (!this.previousElementSibling.value) {
        alert("tracking number is null");
        return;
      }
      window.open(
        `https://t.17track.net/en#nums=${this._inputValue()}`,
        "_blank"
      );
    });
  }

  _inputValue() {
    return this.previousElementSibling.value;
  }
}

window.customElements.define("jump-button", JumpButton, { extends: "button" });

// 显示隐藏文案
class MoreTextToggle extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    if (this.toggleContent.clientHeight > this.fullContent[0].clientHeight) {
      this.removeAttribute("hidden");
    }
    this.addEventListener("click", this.toggleElement.bind(this));
  }

  get toggleContent() {
    return this.previousElementSibling;
  }

  get fullContent() {
    return this.previousElementSibling.children;
  }

  toggleElement() {
    this.innerText =
      this.innerText == "Read More >" ? "See Less >" : "Read More >";
    this.toggleContent.classList.toggle("active");
  }
}
customElements.define("more-text-toggle", MoreTextToggle);

class faqCollapsible extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.collapsibleItem.forEach((item) =>
      item.addEventListener("click", this.collapsible.bind(this))
    );
  }
  get collapsibleItem() {
    return this.querySelectorAll(".item");
  }
  collapsible(e) {
    e.target.classList.toggle("active");
    const content_height = e.target.nextElementSibling.scrollHeight;
    if (e.target.classList.contains("active")) {
      e.target.nextElementSibling.style.height = `${content_height}px`;
    } else {
      e.target.nextElementSibling.style.height = 0;
    }
  }
}
customElements.define("faq-collapsible", faqCollapsible);

// copy to clipboard
class CopyToClipboard extends HTMLElement {
  constructor() {
    super();
    this.style.cssText =
      "display: inline-block; width: 20px; height: 20px; cursor: pointer; vertical-align: middle; text-align: center";
    this.innerHTML = this.copyIcon;
  }
  connectedCallback() {
    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("copy")) {
        navigator.clipboard.writeText(
          this.copyText.split(":").length > 1
            ? this.copyText.split(":")[1]
            : this.copyText.split(":")[0]
        );
        this.innerHTML = this.copySuccessIcon;
        setTimeout(() => {
          this.innerHTML = this.copyIcon;
        }, 3000);
      }
    });
  }
  get copyIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" class="copy" width="18" height="18" role="img"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2m-6 12h8a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-8a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2Z"></path></svg>`;
  }
  get copySuccessIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" fill="#57a528" viewBox="0 0 20 20" class="copy-success" width="18" height="18" role="img"><path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.293a1 1 0 0 0-1.414-1.414L9 10.586 7.707 9.293a1 1 0 0 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z" clip-rule="evenodd"></path></svg>`;
  }
  get copyText() {
    return this.previousElementSibling.innerText;
  }
}
customElements.define("copy-to-clipboard", CopyToClipboard);

// image-with-text read more
class ReadMore extends HTMLElement {
  constructor() {
    super();
    this.innerText = "Read More >";
    this.childElements = this.previousElementSibling.children;
    this.collapseContent = JSON.parse(
      JSON.stringify(this.previousElementSibling.innerHTML)
    );
  }
  connectedCallback() {
    this.toggleDom();
    this.resizeWidth();
    this.toggleContent();
  }
  resizeWidth() {
    window.addEventListener("resize", this.toggleDom.bind(this));
  }
  toggleDom() {
    if (window.innerWidth > 980) {
      this.setAttribute("hidden", "");
      this.previousElementSibling.innerHTML = this.collapseContent;
    } else {
      this.removeAttribute("hidden");
      this.previousElementSibling.removeAttribute("open");
      const collapsedParaNum = this.getAttribute(
        "data-collapsed-paragraph-num"
      );
      Array.from(this.childElements)
        .slice(collapsedParaNum)
        .forEach((p) => p.classList.add("mobile-hidden"));
    }
  }
  toggleContent() {
    this.addEventListener("click", () => {
      Array.from(this.childElements)
        .slice(1)
        .forEach((p) => p.classList.toggle("mobile-hidden"));
      this.previousElementSibling.toggleAttribute("open");
      this.innerText = this.previousElementSibling.hasAttribute("open")
        ? "Read Less >"
        : "Read More >";
    });
  }
}
customElements.define("read-more", ReadMore);

class BannerCollection extends HTMLElement {
  constructor() {
    super();
    this.flickity = []; // 存储 Flickity 实例
    this.handleResize = this.initFlickity.bind(this); // 绑定上下文
  }

  connectedCallback() {
    this.initFlickity();
    window.addEventListener("resize", this.handleResize);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResize); // 清理事件监听器
    this.destroyFlickity(); // 组件移除时销毁 Flickity 实例
  }

  initFlickity() {
    Array.from(this.children).forEach((cli, i) => {
      if (
        cli.hasAttribute("pc-flickity") &&
        cli.hasAttribute("mobile-flickity")
      ) {
        this.createFlickity(cli, i);
      } else {
        this.resizeWidth(cli, i);
      }
    });
  }

  createFlickity(cli, index) {
    const ele = cli.querySelector("[flickity-config]");
    try {
      const config = JSON.parse(ele.getAttribute("flickity-config"));
      this.flickity[index] = new Flickity(ele, config);
      this.flickity[index].on("select", (i) => this.toggleTextBox(i));
    } catch (error) {
      console.error("Flickity config parsing error:", error);
    }
  }

  resizeWidth(cli, index) {
    const isMobile = window.innerWidth <= 999;
    const hasMobileFlickity = cli.hasAttribute("mobile-flickity");
    const hasPcFlickity = cli.hasAttribute("pc-flickity");

    if ((isMobile && hasMobileFlickity) || (!isMobile && hasPcFlickity)) {
      this.createFlickity(cli, index);
    } else {
      this.destroyFlickityAt(index);
      if (this.hasAttribute("hover-animation")) {
        this.hoverSlide();
      }
    }
  }

  hoverSlide() {
    this.querySelectorAll(".textHoverBox .text").forEach((t, i) => {
      const box = t.querySelector(".box");
      box.addEventListener("mouseenter", () => this.showAnimation(box, i));
      box.addEventListener("mouseleave", () => this.hideAnimation(box));
    });
  }

  showAnimation(box, index) {
    this.querySelectorAll(".imageSwitchBox .image").forEach((image) =>
      image.classList.remove("active")
    );
    this.querySelectorAll(".imageSwitchBox .image")[index].classList.add(
      "active"
    );
    box.parentElement.classList.add("on");
  }

  hideAnimation(box) {
    box.parentElement.classList.remove("on");
  }

  toggleTextBox(index) {
    this.querySelectorAll(".textHoverBox .text").forEach((t, i) => {
      t.classList.toggle("active", i === index);
    });
  }

  destroyFlickityAt(index) {
    if (this.flickity[index]) {
      this.flickity[index].destroy();
      this.flickity[index] = null; // 清空引用
    }
  }

  destroyFlickity() {
    this.flickity.forEach((item, index) => {
      if (item) {
        item.destroy();
        this.flickity[index] = null; // 清空引用
      }
    });
  }
}
window.customElements.define("banner-collection", BannerCollection);
